// Define types locally since they're not exported from @/types
export interface RawMaterial {
  id: string;
  name: string;
  unit_type: string;
  current_stock: number;
  cost_per_unit: number;
  supplier?: string;
}

export interface InventoryTransaction {
  id: string;
  material_id: string;
  transaction_type: 'restock' | 'deduction';
  quantity: number;
  cost?: number;
  created_at: string;
  user_id: string;
}

export interface MenuItem {
  id: string;
  name: string;
  base_price: number;
  description?: string;
}

export interface RecipeIngredient {
  id: string;
  material_id: string;
  quantity: number;
  unit: string;
}

export interface Recipe {
  id: string;
  menu_item_id: string;
  name: string;
  description?: string;
  portion_size: number;
  recipe_ingredients: RecipeIngredient[];
}

export interface WasteCalculationResult {
  materialWaste: Map<string, number>; // material_id -> waste amount
  menuItemWaste: Map<string, number>; // menu_item_id -> waste amount
  materialWasteDetails: Map<string, MaterialWasteDetail>; // material_id -> detailed waste info
}

export interface MaterialWasteDetail {
  materialId: string;
  materialName: string;
  totalRestocked: number;
  totalUsed: number;
  totalWaste: number;
  wastePercentage: number;
  restockCycles: RestockCycle[];
  lastRestockDate: string;
  restockCyclesCount: number;
  currentStock: number;
}

export interface RestockCycle {
  restockDate: string;
  restockQuantity: number;
  usedInCycle: number;
  wasteInCycle: number;
  cycleDuration: number; // days
}

export interface MenuItemWasteDetail {
  menuItemId: string;
  menuItemName: string;
  totalWaste: number;
  wasteByMaterial: Map<string, number>; // material_id -> waste amount
  efficiencyScore: number;
}

/**
 * Standardized waste calculation using Restock Cycle Analysis
 * This is the single source of truth for all waste calculations
 * 
 * @param materials - Raw materials data (should be filtered by branch if needed)
 * @param transactions - Inventory transactions (should be filtered by branch if needed)
 * @param menuItems - Menu items (should be filtered by branch if needed)
 * @param recipes - Recipes data
 * @param options - Optional configuration
 * @param options.branchId - Branch ID for filtering (if not provided, assumes all data is already filtered)
 * @param options.timeRangeDays - Time range in days for calculations (default: all available data)
 * @param options.includeCurrentStock - Whether to include current stock in calculations (default: true)
 */
export function calculateWasteUsingRestockCycles(
  materials: RawMaterial[],
  transactions: InventoryTransaction[],
  menuItems: MenuItem[],
  recipes: Recipe[],
  options?: {
    branchId?: string;
    timeRangeDays?: number;
    includeCurrentStock?: boolean;
  }
): WasteCalculationResult {
  const materialWaste = new Map<string, number>();
  const menuItemWaste = new Map<string, number>();
  const materialWasteDetails = new Map<string, MaterialWasteDetail>();

  // Initialize menu item waste map
  menuItems.forEach(item => menuItemWaste.set(item.id, 0));

  // Filter transactions by time range if specified
  let filteredTransactions = transactions;
  if (options?.timeRangeDays) {
    const cutoffDate = new Date();
    cutoffDate.setDate(cutoffDate.getDate() - options.timeRangeDays);
    filteredTransactions = transactions.filter(t => 
      new Date(t.created_at) >= cutoffDate
    );
  }

  // Calculate waste for each material
  materials.forEach(material => {
    const materialDetail = calculateMaterialWaste(material, filteredTransactions, options);
    materialWaste.set(material.id, materialDetail.totalWaste);
    materialWasteDetails.set(material.id, materialDetail);

    // Distribute waste to menu items that use this material
    distributeWasteToMenuItems(
      material,
      materialDetail.totalWaste,
      menuItems,
      recipes,
      menuItemWaste
    );
  });

  return {
    materialWaste,
    menuItemWaste,
    materialWasteDetails
  };
}

/**
 * Calculate waste for a single material using restock cycle analysis
 */
function calculateMaterialWaste(
  material: RawMaterial,
  transactions: InventoryTransaction[],
  options?: {
    branchId?: string;
    timeRangeDays?: number;
    includeCurrentStock?: boolean;
  }
): MaterialWasteDetail {
  // Get material-specific transactions
  const materialTransactions = transactions.filter(t => t.material_id === material.id);
  
  // Sort transactions by date
  const restockTransactions = materialTransactions
    .filter(t => t.transaction_type === 'restock')
    .sort((a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime());
  
  const deductionTransactions = materialTransactions
    .filter(t => t.transaction_type === 'deduction')
    .sort((a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime());

  let totalRestocked = 0;
  let totalUsed = 0;
  let totalWaste = 0;
  const restockCycles: RestockCycle[] = [];

  // Handle case where there are no restock transactions but there are deduction transactions
  if (restockTransactions.length === 0 && deductionTransactions.length > 0) {
    // Calculate total used from all deduction transactions
    totalUsed = deductionTransactions.reduce((sum, d) => sum + d.quantity, 0);
    totalRestocked = 0; // No restocks recorded
    totalWaste = 0; // Can't calculate waste without restock data
    
    // Create a single "cycle" representing the entire period
    if (deductionTransactions.length > 0) {
      const firstDeduction = deductionTransactions[0];
      const lastDeduction = deductionTransactions[deductionTransactions.length - 1];
      const cycleDuration = Math.ceil(
        (new Date(lastDeduction.created_at).getTime() - new Date(firstDeduction.created_at).getTime()) / (1000 * 60 * 60 * 24)
      );
      
      restockCycles.push({
        restockDate: firstDeduction.created_at,
        restockQuantity: 0,
        usedInCycle: totalUsed,
        wasteInCycle: 0,
        cycleDuration: Math.max(1, cycleDuration)
      });
    }
  } else {
    // Original logic for materials with restock transactions
    // Calculate waste for each restock cycle
    for (let i = 0; i < restockTransactions.length; i++) {
      const restock = restockTransactions[i];
      const restockDate = new Date(restock.created_at);
      const restockQuantity = restock.quantity;
      
      totalRestocked += restockQuantity;

      // Find the next restock date (or use current date if this is the last restock)
      const nextRestockDate = i < restockTransactions.length - 1 
        ? new Date(restockTransactions[i + 1].created_at)
        : new Date();
      
      // Get deductions that occurred between this restock and the next restock
      const cycleDeductions = deductionTransactions.filter(d => {
        const deductionDate = new Date(d.created_at);
        return deductionDate >= restockDate && deductionDate < nextRestockDate;
      });
      
      // Calculate total used in this cycle
      const usedInCycle = cycleDeductions.reduce((sum, d) => sum + d.quantity, 0);
      totalUsed += usedInCycle;
      
      // Calculate waste for this cycle
      const wasteInCycle = Math.max(0, restockQuantity - usedInCycle);
      totalWaste += wasteInCycle;
      
      // Calculate cycle duration
      const cycleDuration = Math.ceil((nextRestockDate.getTime() - restockDate.getTime()) / (1000 * 60 * 60 * 24));
      
      restockCycles.push({
        restockDate: restock.created_at,
        restockQuantity,
        usedInCycle,
        wasteInCycle,
        cycleDuration
      });
    }
  }

  // Calculate waste percentage
  const wastePercentage = totalRestocked > 0 ? (totalWaste / totalRestocked) * 100 : 0;
  
  // Get last restock date
  const lastRestockDate = restockCycles.length > 0 
    ? restockCycles[restockCycles.length - 1].restockDate 
    : '';

  return {
    materialId: material.id,
    materialName: material.name,
    totalRestocked,
    totalUsed,
    totalWaste,
    wastePercentage,
    restockCycles,
    lastRestockDate,
    restockCyclesCount: restockCycles.length,
    currentStock: material.current_stock
  };
}

/**
 * Distribute material waste among menu items that use that material
 * Uses weighted distribution based on actual recipe usage
 */
function distributeWasteToMenuItems(
  material: RawMaterial,
  totalWaste: number,
  menuItems: MenuItem[],
  recipes: Recipe[],
  menuItemWaste: Map<string, number>
): void {
  if (totalWaste <= 0) return;

  // Find menu items that use this material
  const itemsUsingMaterial = menuItems.filter(item => {
    const recipe = recipes.find(r => r.menu_item_id === item.id);
    return recipe && recipe.recipe_ingredients?.some((ing: RecipeIngredient) => 
      ing.material_id === material.id
    );
  });

  if (itemsUsingMaterial.length === 0) return;

  // Calculate total usage of this material across all menu items
  let totalUsage = 0;
  const itemUsage = new Map<string, number>();

  itemsUsingMaterial.forEach(item => {
    const recipe = recipes.find(r => r.menu_item_id === item.id);
    const ingredient = recipe?.recipe_ingredients?.find((ing: RecipeIngredient) => 
      ing.material_id === material.id
    );
    
    if (ingredient) {
      const usage = ingredient.quantity;
      itemUsage.set(item.id, usage);
      totalUsage += usage;
    }
  });

  // Distribute waste based on usage proportion
  if (totalUsage > 0) {
    itemsUsingMaterial.forEach(item => {
      const usage = itemUsage.get(item.id) || 0;
      const usageProportion = usage / totalUsage;
      const itemWaste = totalWaste * usageProportion;
      
      const currentWaste = menuItemWaste.get(item.id) || 0;
      menuItemWaste.set(item.id, currentWaste + itemWaste);
    });
  } else {
    // Fallback to equal distribution if no usage data
    const wastePerItem = totalWaste / itemsUsingMaterial.length;
    itemsUsingMaterial.forEach(item => {
      const currentWaste = menuItemWaste.get(item.id) || 0;
      menuItemWaste.set(item.id, currentWaste + wastePerItem);
    });
  }
}

/**
 * Validate waste calculation data for accuracy and completeness
 */
export function validateWasteCalculationData(
  materials: RawMaterial[],
  transactions: InventoryTransaction[],
  menuItems: MenuItem[],
  recipes: Recipe[]
): { isValid: boolean; errors: string[]; warnings: string[] } {
  const errors: string[] = [];
  const warnings: string[] = [];

  // Check for missing data
  if (!materials || materials.length === 0) {
    errors.push('No materials found for waste calculation');
  }

  if (!transactions || transactions.length === 0) {
    warnings.push('No transactions found - waste calculation may be incomplete');
  }

  if (!menuItems || menuItems.length === 0) {
    warnings.push('No menu items found - menu item waste will be zero');
  }

  if (!recipes || recipes.length === 0) {
    warnings.push('No recipes found - menu item waste distribution may be inaccurate');
  }

  // Check for data quality issues
  materials.forEach(material => {
    const materialTransactions = transactions.filter(t => t.material_id === material.id);
    
    if (materialTransactions.length === 0) {
      warnings.push(`No transactions found for material: ${material.name}`);
    }

    // Check for negative stock
    if (material.current_stock < 0) {
      errors.push(`Negative stock detected for material: ${material.name}`);
    }

    // Check for suspicious waste patterns
    const restockTransactions = materialTransactions.filter(t => t.transaction_type === 'restock');
    const deductionTransactions = materialTransactions.filter(t => t.transaction_type === 'deduction');
    
    const totalRestocked = restockTransactions.reduce((sum, t) => sum + t.quantity, 0);
    const totalDeducted = deductionTransactions.reduce((sum, t) => sum + t.quantity, 0);
    
    if (totalDeducted > totalRestocked + material.current_stock) {
      warnings.push(`Possible data inconsistency for material: ${material.name} - deductions exceed restocks + current stock`);
    }
  });

  // Check for orphaned transactions
  const materialIds = new Set(materials.map(m => m.id));
  const orphanedTransactions = transactions.filter(t => !materialIds.has(t.material_id));
  
  if (orphanedTransactions.length > 0) {
    warnings.push(`${orphanedTransactions.length} transactions reference non-existent materials`);
  }

  return {
    isValid: errors.length === 0,
    errors,
    warnings
  };
}

/**
 * Calculate waste efficiency score for a menu item (0-100)
 * Higher score = less waste
 */
export function calculateWasteEfficiencyScore(
  menuItemWaste: number,
  totalProduction: number,
  basePrice: number
): number {
  if (totalProduction === 0) return 100; // No production = no waste
  
  // Calculate waste per unit
  const wastePerUnit = menuItemWaste / totalProduction;
  
  // Calculate waste cost impact
  const wasteCost = wastePerUnit * basePrice;
  const wasteCostPercentage = (wasteCost / basePrice) * 100;
  
  // Convert to efficiency score (0-100)
  // 0% waste cost = 100 efficiency
  // 50% waste cost = 0 efficiency
  const efficiencyScore = Math.max(0, Math.min(100, 100 - (wasteCostPercentage * 2)));
  
  return Math.round(efficiencyScore);
} 